<template>
  <div class="mb-2 text-left" v-if="visible">
    <!-- Title -->
    <span>
      {{ title }}
      <span v-if="required" class="text-danger">*</span>
    </span>

    <!-- v-select -->
    <v-select
      :options="options"
      :reduce="reduceFunction"
      v-model="localValue"
      :dropdown-should-open="dropdownShouldOpen"
      :label="labelKey"
      :track-by="trackByKey"
      :placeholder="placeholder"
      class="style-chooser mt-2"
      :disabled="disabled"
      @input="handleInput"
    >
      <!-- Template para truncar texto largo con tooltip -->
      <template #selected-option="option">
        <span
          class="selected-option-vue-select"
          v-b-tooltip.left.v-secondary.noninteractive="{
            title: `${showTooltip ? option[labelKey] : ''}`,
            boundary: 'viewport',
          }"
          @mouseenter="checkTextWidth($event)"
        >
          {{ option[labelKey] }}
        </span>
      </template>

      <!-- Template para "no options" -->
      <template v-slot:no-options="{ search, searching }">
        <template v-if="searching">
          {{ $t("reusable.aiComponent.noResultsFor") }}: "<em>{{ search }}</em
          >"
        </template>
        <template v-else>
          {{ $t("reusable.common.noOptions") }}
        </template>
      </template>
    </v-select>
  </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "GenericSelector",
  components: {
    vSelect,
  },
  props: {
    // Title del selector
    title: {
      type: String,
      required: true,
    },
    // Array de opciones para el selector
    options: {
      type: Array,
      default: () => [],
    },
    // Valor seleccionado (v-model)
    value: {
      type: [Number, String, Object, null],
      default: null,
    },
    // Key del objeto que se usa como label visible
    labelKey: {
      type: String,
      default: "name",
    },
    // Key del objeto que se usa para el tracking
    trackByKey: {
      type: String,
      default: "id",
    },
    // Key del objeto que se usa como valor (reduce)
    reduceKey: {
      type: String,
      default: "id",
    },
    // Placeholder del selector
    placeholder: {
      type: String,
      default: function () {
        return this.$t("reusable.aiComponent.selectOption");
      },
    },
    // Si el selector está deshabilitado
    disabled: {
      type: Boolean,
      default: false,
    },
    // Si el campo es requerido (muestra asterisco rojo)
    required: {
      type: Boolean,
      default: false,
    },
    //Si el selector es visible
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      localValue: this.value,
      showTooltip: false,
    };
  },
  computed: {
    // Función que extrae el valor del objeto seleccionado
    reduceFunction() {
      return (option) => option[this.reduceKey];
    },
  },
  methods: {
    // Controla si el dropdown debe abrirse
    dropdownShouldOpen(VueSelect) {
      if (this.options.length > 0) {
        return VueSelect.open;
      }
      return VueSelect.search.length !== 0 && VueSelect.open;
    },
    // Maneja el cambio de valor
    handleInput(value) {
      this.localValue = value;
      this.$emit("input", value);
      this.$emit("change", value);
    },

    // Detecta si el texto está truncado para mostrar tooltip
    checkTextWidth(event) {
      this.$nextTick(() => {
        const element = event.target; // Permite acceder al elemento exacto, sin necesidad de usar refs
        if (element) {
          this.showTooltip = element.scrollWidth > element.clientWidth;
        }
      });
    },
  },
  watch: {
    // Sincroniza el valor interno cuando cambia desde el padre
    value(newValue) {
      this.localValue = newValue;
    },
  },
};
</script>

<style scoped>
/* Truncado de texto con ellipsis */
.selected-option-vue-select {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}
/* Estilos relacionados con vue-select */
.style-chooser >>> {
  --vs-controls-color: #212020c0;
  --vs-actions-padding: 0px 4px 0px 0px;
  --vs-dropdown-max-height: 125px;
}
.style-chooser >>> .vs__search {
  padding: 0px !important;
  width: 0px !important;
}
.style-chooser >>> .vs__selected-options {
  /* Quita el espacio en blanco debajo del texto cuando se selecciona una opcion */
  flex-wrap: nowrap;
}
.style-chooser .vs__open-indicator {
  margin-top: 20px;
}
</style>